import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { API } from '../api/endpoints';
import { fetchNcNotifications } from '../api/pdpmApi';
import {
  useAlertsStore,
  ALERT_CATEGORY,
  ALERT_SEVERITY,
  type Alert,
  type AlertCategory,
} from '../stores/alertsStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useGlobalStore } from '../stores/globalStore';
import { useSessionStore } from '../stores/sessionStore';
import { useEhrActionStore } from '../stores/ehrActionStore';
import { summarizePending } from '../pages/emr/pendingStatus';
import type { TblPatientPending } from '../types/emr';
import { getPendingStatus, PENDING_STATUS } from '../types/emr';
import type { BedsideNotOrderedPatient } from '../api/dashboardApi';

/** How often to poll for new alerts (60 seconds) */
const POLL_INTERVAL_MS = 60_000;

interface BedsideHelpRequestAlert {
  pKey_HelpRequest: number;
  patientName: string;
  room_No: number | null;
  bed_No: string;
  deviceName: string;
  requestType: string;
  message: string;
  createdDate: string;
}

/**
 * One Nutrition Compliance event for the bell (api/Pdpm/Notifications): a finding raised, reopened, become
 * critical or assigned. No resident name or clinical value; notifyKey changes only when something new happens to
 * the finding, so the same event is never announced twice however often the bell polls or the page opens.
 */
interface NcNotificationPollItem {
  findingId: number;
  notifyKey: string;
  title: string;
  kind: string;
  severity: string;
  category: string;
  date: string;
  assignedToMe: boolean;
}

interface AlertPollData {
  bedsideHelpRequests: BedsideHelpRequestAlert[];
  notOrderedPatients: BedsideNotOrderedPatient[];
  notOrderedMealtime: number;
  pdpmAlerts: NcNotificationPollItem[];
  ehrPending: TblPatientPending[];
}

/**
 * How long a healthy EHR message may sit unprocessed before it is a problem.
 *
 * Count alone is a bad signal here — a dozen pending messages on a busy
 * morning is completely normal. Age is what distinguishes a working queue from
 * a worker that has stopped, which is the failure that leaves stale diet
 * orders in place while every screen looks correct.
 */
const EHR_STALE_HOURS = 24;

/**
 * How long a new message may show "no match" before it is a problem.
 *
 * A message is stored with MatchFound computed at download (an admission, or a
 * resident already on file). The worker works the match out again on its next
 * pass (EHR_Console Fill_dtPatientPending: update action, cancels, discharges),
 * which is a few seconds on a quiet queue. Raising it the moment it arrives
 * flagged messages that matched and processed a minute later. Five minutes
 * covers a pass on a busy queue; a real no-match still surfaces within minutes.
 * Mapping errors need no wait: only that pass sets them.
 */
const EHR_NO_MATCH_GRACE_HOURS = 5 / 60;

function hoursSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return (Date.now() - then) / 3_600_000;
}

function makeId(category: AlertCategory, key: string | number): string {
  return `${category}-${key}`;
}

/**
 * Routes where a toast must not appear over the screen.
 *
 * Selective Touch is a tray-line kiosk. Someone is standing at a cart taking a
 * resident's meal selections on a touch screen, and a toast lands on top of
 * the menu they are tapping — over the very buttons in play, for eight
 * seconds, mid-order. None of the alerts we raise is theirs to act on either:
 * a stuck EHR queue or a PDPM documentation gap is back-office work for the
 * dietitian, not for the aide serving lunch.
 *
 * The alerts still go into the bell, so nothing is lost — they are just not
 * shoved in front of a person doing something else.
 */
function isQuietRoute(): boolean {
  return window.location.pathname.startsWith('/selective-touch');
}

/**
 * Builds the bell's alerts.
 *
 * The bell is an EXCEPTION LIST, not an event feed. It carries only problems
 * that have no dashboard card of their own — see `HAS_A_CARD` in
 * `pages/dashboard/AttentionRows.tsx` for the same rule stated from the
 * dashboard's side.
 *
 * It used to raise one alert per admission, discharge, allergy change, diet
 * order change and tray line warning in a 24-hour window. Every one of those
 * has a card on the dashboard that lays the same rows out in a browsable grid,
 * so the bell became a second, worse copy of the dashboard: a real facility
 * showed 151 alerts and a 99+ badge, which buries the handful of items that
 * genuinely need someone — a stopped EHR worker, an unanswered help request —
 * under a wall of routine activity, and trains people to ignore the badge.
 *
 * So: if a category has a dashboard card, it does not belong here. Adding one
 * back means adding an alert that is already on screen somewhere else.
 */
function buildAlerts(data: AlertPollData): Alert[] {
  const alerts: Alert[] = [];
  const now = new Date().toISOString();

  // Bedside help requests - critical (someone needs help NOW)
  for (const h of data.bedsideHelpRequests) {
    const room = h.room_No ? `Room ${h.room_No}` : '';
    const bed = h.bed_No ? ` Bed ${h.bed_No}` : '';
    const who = h.patientName || h.deviceName || 'Unknown';
    alerts.push({
      id: makeId(ALERT_CATEGORY.bedsideHelp, h.pKey_HelpRequest),
      category: ALERT_CATEGORY.bedsideHelp,
      severity: ALERT_SEVERITY.critical,
      title: 'Help Request',
      message: `${who} - ${room}${bed}${h.message ? `: ${h.message}` : ''}`,
      timestamp: h.createdDate || now,
      read: false,
      link: '/settings?tab=bedside',
      sourceKey: `bedside-help-${h.pKey_HelpRequest}`,
    });
  }

  // Not-ordered patients — warning (only if there are patients who haven't ordered)
  if (data.notOrderedPatients.length > 0) {
    const count = data.notOrderedPatients.length;
    const mealtimeName = data.notOrderedMealtime === 1 ? 'Breakfast' : data.notOrderedMealtime === 2 ? 'Lunch' : 'Dinner';
    const firstThree = data.notOrderedPatients.slice(0, 3).map((p) => p.fullName).join(', ');
    const remaining = count > 3 ? ` and ${count - 3} more` : '';
    alerts.push({
      id: makeId(ALERT_CATEGORY.notOrdered, `${data.notOrderedMealtime}-${count}`),
      category: ALERT_CATEGORY.notOrdered,
      severity: ALERT_SEVERITY.warning,
      title: `${count} patient${count !== 1 ? 's' : ''} not ordered for ${mealtimeName}`,
      message: `${firstThree}${remaining}`,
      timestamp: now,
      read: false,
      link: '/selective-touch',
      sourceKey: `notordered-${data.notOrderedMealtime}-${count}`,
    });
  }

  // Nutrition Compliance: genuinely new events only (raised, reopened, became critical, assigned to you).
  // Critical clinical findings are critical in the bell (and toast); the rest are warnings. No resident names.
  for (const n of data.pdpmAlerts) {
    const severity = n.severity === 'critical' && n.category !== 'Documentation' && n.category !== 'DataQuality'
      ? ALERT_SEVERITY.critical
      : ALERT_SEVERITY.warning;
    alerts.push({
      id: makeId(ALERT_CATEGORY.pdpmGap, n.notifyKey),
      category: ALERT_CATEGORY.pdpmGap,
      severity,
      title: n.title,
      message: n.assignedToMe && n.kind !== 'Assigned to you' ? `${n.kind} (assigned to you)` : n.kind,
      timestamp: n.date || now,
      read: false,
      link: `/pdpm?finding=${n.findingId}`,
      sourceKey: n.notifyKey,
    });
  }

  // --- EHR messages that did not land ---------------------------------------
  // Grouped rather than one alert per message: a stopped worker produces
  // hundreds at once, and 300 individually-toasted alerts is the same as none.
  // One alert per failure MODE, carrying the count and the oldest timestamp.
  {
    const unmatched: TblPatientPending[] = [];
    const mappingErrors: TblPatientPending[] = [];
    const stale: TblPatientPending[] = [];

    for (const msg of data.ehrPending) {
      switch (getPendingStatus(msg)) {
        case PENDING_STATUS.NO_MATCH:
          if (hoursSince(msg.dateEntered) >= EHR_NO_MATCH_GRACE_HOURS) unmatched.push(msg);
          break;
        case PENDING_STATUS.MAPPING_ERROR:
          mappingErrors.push(msg);
          break;
        default:
          // Nothing is wrong with the message; nothing has picked it up.
          if (hoursSince(msg.dateEntered) >= EHR_STALE_HOURS) stale.push(msg);
      }
    }

    const oldest = (list: TblPatientPending[]) =>
      list.reduce<string>((acc, m2) => {
        const d = m2.dateEntered;
        return !acc || (d && d < acc) ? (d ?? acc) : acc;
      }, '');

    const ageLabel = (list: TblPatientPending[]) => {
      const h = hoursSince(oldest(list));
      if (h < 1) return 'just now';
      if (h < 48) return `oldest ${Math.floor(h)}h old`;
      return `oldest ${Math.floor(h / 24)} days old`;
    };

    const groups: {
      key: string;
      list: TblPatientPending[];
      title: string;
      severity: typeof ALERT_SEVERITY.critical | typeof ALERT_SEVERITY.warning;
    }[] = [
      // Unmatched and unmapped are hard stops: nothing will be applied until a
      // person intervenes, however long they wait.
      { key: 'nomatch', list: unmatched, title: 'EHR messages not matched', severity: ALERT_SEVERITY.critical },
      { key: 'mapping', list: mappingErrors, title: 'EHR mapping errors', severity: ALERT_SEVERITY.critical },
      { key: 'stale', list: stale, title: 'EHR messages unprocessed', severity: ALERT_SEVERITY.warning },
    ];

    for (const g of groups) {
      if (g.list.length === 0) continue;
      alerts.push({
        id: makeId(ALERT_CATEGORY.ehrMessage, g.key as unknown as number),
        category: ALERT_CATEGORY.ehrMessage,
        severity: g.severity,
        title: g.title,
        message: `${g.list.length} message${g.list.length === 1 ? '' : 's'} · ${ageLabel(g.list)}`,
        timestamp: oldest(g.list) || now,
        read: false,
        link: '/emr',
        // One key per failure MODE, with the count deliberately NOT in it.
        //
        // The count used to be part of the key so the alert would re-fire as
        // the backlog grew. What that actually did on a site with a genuinely
        // stuck queue (Hialeah sat at thousands of stuck admissions) was
        // mint a brand-new alert every time the number moved: read state is
        // tracked by sourceKey, so the bell went unread again, and an explicit
        // dismissal only ever silenced the exact count that was on screen when
        // the user clicked it. The alert came straight back a minute later.
        //
        // A stable key means the row updates in place — the count and age in
        // `message` still climb where you can see them — and "I've seen this"
        // and "dismiss this" mean what they say.
        sourceKey: `ehr-${g.key}`,
      });
    }
  }

  // Sort by timestamp descending (newest first)
  alerts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return alerts;
}

/**
 * Hook that polls the dashboard endpoints for critical events and populates the alerts store.
 * Also triggers toast notifications for newly discovered critical alerts.
 *
 * Should be mounted once in MainLayout.
 */
export function useAlertPolling() {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const isSessionExpired = useSessionStore((s) => s.isSessionExpired);
  // EHR alerts only for someone who can open the EHR pages (Dietech_Ehr_Level > 0,
  // as WinForms' ribbon) at a facility with EHR active. The API refuses the pending
  // list to anyone else, so polling it for them was a failed request a minute.
  const canSeeEhr = useGlobalStore((s) =>
    (s.userRole?.dietech_Ehr_Level ?? 0) > 0
    && !!(s.facilitySettings as Record<string, unknown> | null)?.facilityEhrActive);
  // Nutrition Compliance events only for someone who can open its page (Dietech_Pdpm_Level > 0,
  // the same field the /pdpm route checks). The API refuses the alert list to anyone
  // else, and no role had the level when permissions went to Enforce, so polling it
  // for everyone was a refused request a minute per signed-in user.
  const canSeePdpm = useGlobalStore((s) => (s.userRole?.dietech_Pdpm_Level ?? 0) > 0);
  const initialLoadDone = useRef(false);

  const query = useQuery({
    queryKey: ['alerts', 'poll', facilityId, canSeeEhr, canSeePdpm],
    queryFn: async (): Promise<AlertPollData> => {
      // Determine current mealtime based on hour: before 10 = breakfast, before 15 = lunch, else dinner
      const hour = new Date().getHours();
      const currentMealtime = hour < 10 ? 1 : hour < 15 ? 2 : 3;

      // The five dashboard endpoints (admitted, discharged, allergies, diet
      // orders, tray line) used to be fetched here too, duplicating what
      // `api/dashboardApi.ts` already fetches for the cards — five extra
      // requests a minute, per user, on every page, to fill the bell with a
      // copy of the dashboard. The bell no longer shows those categories, so
      // it no longer asks for them.
      const [pdpmAlertSummary, ehrPending] = await Promise.all([
        canSeePdpm
          ? fetchNcNotifications(facilityId).catch(() => [])
          : Promise.resolve([]),
        canSeeEhr
          ? apiClient.get<TblPatientPending[]>(API.emrPending(facilityId))
            .then((r) => {
              const rows = r.data ?? [];
              // Feeds the EHR badge in the sidebar from this same request.
              // Only on success: a failed poll must not read as "nothing to do".
              useEhrActionStore.getState().setActionNeeded(facilityId, summarizePending(rows).actionNeededCount);
              return rows;
            })
            .catch(() => [] as TblPatientPending[])
          : Promise.resolve([] as TblPatientPending[]),
      ]);
      // Bedside endpoints (Health, Help, NotOrdered) require device-token
      // auth and always 401 for regular users — don't poll them here.
      return {
        bedsideHelpRequests: [],
        notOrderedPatients: [],
        notOrderedMealtime: currentMealtime,
        pdpmAlerts: pdpmAlertSummary as NcNotificationPollItem[],
        ehrPending,
      };
    },
    enabled: facilityId > 0 && !isSessionExpired,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS / 2,
  });

  // Process poll results into alerts store + fire toasts for new critical items
  useEffect(() => {
    if (!query.data) return;

    const alerts = buildAlerts(query.data);
    const newIds = useAlertsStore.getState().setAlerts(alerts);

    // On very first load, don't toast - user hasn't seen anything yet, just populate the bell
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      // Mark all initial alerts as "toasted" so they don't fire toasts
      useAlertsStore.getState().markToasted(alerts.map((a) => a.id));
      return;
    }

    if (newIds.length === 0) return;

    // Deliberately checked without marking anything toasted: an alert raised
    // while the kiosk is up is still new when whoever left it goes back to a
    // desk screen, and that is when it should surface.
    if (isQuietRoute()) return;

    // Toast only new critical alerts
    const { toastedIds, markToasted } = useAlertsStore.getState();
    const toToast = alerts.filter(
      (a) => newIds.includes(a.id) && a.severity === 'critical' && !toastedIds.has(a.id),
    );

    if (toToast.length > 0) {
      const addNotification = useNotificationStore.getState().addNotification;
      // Show max 3 toasts to avoid flooding
      for (const alert of toToast.slice(0, 3)) {
        addNotification({
          message: `${alert.title}: ${alert.message}`,
          type: alert.severity === 'critical' ? 'warning' : 'info',
          duration: 8000,
        });
      }
      if (toToast.length > 3) {
        addNotification({
          message: `+${toToast.length - 3} more new alerts`,
          type: 'info',
          duration: 5000,
        });
      }
      markToasted(toToast.map((a) => a.id));
    }
  }, [query.data]);
}
